import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { Person } from "@b2bportal/air-booking-api";
import type { TripDetailsV2 } from "@b2bportal/air-exchange-api";
import {
  FlightExchangePolicySelectors,
  FlightExchangeSearchSelectors,
  FlightSearchSelectors,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import { getSliceIndex } from "@b2bportal/core-utilities";
import { Checkout, ParentState } from "@checkout/index";
import type { CheckoutProps, PersonId } from "@hopper-b2b/types";
import { useFeatureFlagsContext } from "@hopper-b2b/utilities";
import { useAuth } from "@hsbc/auth";
import type { FlightExchangeMachineContext } from "@hsbc/flights";

type FlightsExchangeCheckoutProps = Omit<CheckoutProps, "context"> & {
  initialContext: FlightExchangeMachineContext;
};

/**
 * If a new context is added, make sure to update CheckoutProvider's cleanContext to ensure
 * the new values are not reset
 */
export const FlightsExchangeCheckout = (
  props: FlightsExchangeCheckoutProps
) => {
  const {
    actions,
    clientAssets,
    Component,
    getInitialContext,
    guards,
    initialContext,
    onCleanUp,
    onLoad,
    onPathnameChange,
    onStateValueChange,
    services,
    sessionInfo,
    stateMachine,
    validateContext,
  } = props;
  const { userInfo } = useAuth();
  const featureFlags = useFeatureFlagsContext();

  // #region flight exchange selectors
  const exchangePolicy = useSelector(
    FlightExchangePolicySelectors.getExchangePolicy
  );
  const exchangeScenario = useSelector(
    FlightExchangePolicySelectors.getExchangeScenario
  );
  const originalItinerary = useSelector(
    FlightExchangePolicySelectors.getOriginalItinerary
  );
  const exchangeSearch = useSelector(
    FlightExchangeSearchSelectors.getExchangeSearchStore
  );
  const originalPaxInfo = useSelector(
    FlightExchangePolicySelectors.getPassengers
  );
  // #endregion

  // #region flight search selectors
  const departureDate = useSelector(FlightSearchSelectors.getDepartureDate);
  const destination = useSelector(FlightSearchSelectors.getDestination);
  const origin = useSelector(FlightSearchSelectors.getOrigin);
  const returnDate = useSelector(FlightSearchSelectors.getReturnDate);
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);
  // #endregion

  // #region flight shop selectors
  const airlines = useSelector(FlightShopSelectors.airlinesSelector);
  const airports = useSelector(FlightShopSelectors.airportsSelector);
  const destinationCountryCode = useSelector(
    FlightShopSelectors.getDestinationCountryCode
  );
  const originCountryCode = useSelector(
    FlightShopSelectors.getOriginCountryCode
  );
  const selectedTrip = useSelector(FlightShopSelectors.getSelectedTrip);
  const shopPricingInfo = useSelector(
    FlightShopSelectors.shopPricingInfoSelector
  );
  const tripDetailsMap = useSelector(
    FlightShopSelectors.tripDetailsByIdSelector
  );
  let tripDetails: TripDetailsV2 | undefined = undefined;

  if (selectedTrip.tripId != null) {
    tripDetails = tripDetailsMap[selectedTrip.tripId];
  }
  // #endregion

  const { departureLabel, returnLabel } = useMemo(() => {
    let departureLabel = "";
    let returnLabel = "";

    if (tripDetails == null) {
      return { departureLabel, returnLabel };
    }

    const departureSliceIdx = getSliceIndex(true, tripDetails);
    const departureSlice = tripDetails.slices[departureSliceIdx];
    const returnSliceIdx = getSliceIndex(false, tripDetails);
    const hasReturnFlight = returnSliceIdx !== -1;
    const returnSlice = hasReturnFlight
      ? tripDetails.slices[returnSliceIdx]
      : null;

    departureLabel =
      airports?.[departureSlice?.destinationCode].cityName ??
      departureSlice?.destinationName;

    if (returnSlice) {
      returnLabel =
        airports?.[returnSlice.destinationCode].cityName ??
        returnSlice.destinationName;
    }

    return { departureLabel, returnLabel };
  }, [airports, tripDetails]);

  const contextToInitialize = useMemo(() => {
    const lapInfantIds: PersonId[] = [];
    const passengers: Person[] = [];
    const passengerIds: PersonId[] = [];
    let email = "";
    let phoneNumber = "";

    if (originalPaxInfo) {
      const {
        alone,
        contact: { contact },
        withLapInfants,
      } = originalPaxInfo;

      email = contact.emailAddress;
      phoneNumber = contact.phoneNumber;

      for (const pax of alone) {
        passengers.push(pax.person);
        passengerIds.push(pax.person.id);
      }

      for (const adultWithLapInfant of withLapInfants) {
        const { adult, infant } = adultWithLapInfant;

        lapInfantIds.push(infant.person.id);
        passengers.push(adult.person, infant.person);
        passengerIds.push(adult.person.id);
      }
    }

    return {
      ...initialContext,
      featureFlags,
      [ParentState.contactInformation]: {
        ...initialContext[ParentState.contactInformation],
        contactInfo: {
          email,
          phoneNumber,
        },
      },
      [ParentState.passengerInformation]: {
        ...initialContext[ParentState.passengerInformation],
        selectedLapInfantIds: lapInfantIds,
        selectedPassengerIds: passengerIds,
        userPassengers: passengers,
      },
      exchangeContext: {
        exchangePolicy,
        exchangeScenario,
        originalItinerary,
      },
      exchangeSearch: {
        ...exchangeSearch,
      },
      flightSearch: {
        ...(initialContext?.flightSearch ?? {}),
        departureDate,
        destination,
        destinationCountryCode,
        origin,
        originCountryCode,
        returnDate,
        tripCategory,
      },
      flightShop: {
        ...(initialContext.flightShop ?? {}),
        airlines,
        airports,
        departureLabel,
        returnLabel,
        selectedTrip,
        shopPricingInfo,
        tripDetails,
      },
      sessionInfo: {
        ...(initialContext?.sessionInfo ?? {}),
        ...sessionInfo,
        userInfo,
      },
    };
  }, [
    airlines,
    airports,
    departureDate,
    departureLabel,
    destination,
    destinationCountryCode,
    exchangePolicy,
    exchangeScenario,
    exchangeSearch,
    featureFlags,
    initialContext,
    origin,
    originCountryCode,
    originalItinerary,
    originalPaxInfo,
    returnDate,
    returnLabel,
    selectedTrip,
    sessionInfo,
    shopPricingInfo,
    tripCategory,
    tripDetails,
    userInfo,
  ]);

  return (
    <Checkout
      actions={actions}
      Component={Component}
      clientAssets={clientAssets}
      context={contextToInitialize}
      getInitialContext={getInitialContext}
      guards={guards}
      onCleanUp={onCleanUp}
      onLoad={onLoad}
      onPathnameChange={onPathnameChange}
      onStateValueChange={onStateValueChange}
      services={services}
      stateMachine={stateMachine}
      validateContext={validateContext}
    />
  );
};
